import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";
import AuthService from "../AuthService";
import "./SystemOperationsBar.css";

const LABELS = {
  BILLING: "Automatic billing",
  "STATUS CHECK": "Policy status check",
};

const detail = (run) => {
  if (!run) return "Not run yet";
  if (run.status === "RUNNING") {
    const total = Number(run.total_records || 0);
    const processed = Number(run.processed_records || 0);
    const percent =
      run.progress_percent ??
      (total ? Math.round((processed / total) * 100) : 0);
    return total
      ? `${processed} / ${total} · ${percent}%`
      : "Preparing...";
  }
  const time = run.completed_at
    ? new Date(run.completed_at).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
  return `${run.status}${time ? ` · ${time}` : ""}`;
};

function SystemOperationsBar() {
  const user = AuthService.getCurrentUser();
  const permissions = Array.isArray(user?.permissions)
    ? user.permissions
    : [];
  const canOperate =
    user?.access_type === "SYSTEM_USER" &&
    (
      permissions.includes("*") ||
      permissions.includes("run_billing") ||
      permissions.includes("run_status_check") ||
      permissions.includes("fix_policies")
    );
  const canFixBills =
    permissions.includes("*") || permissions.includes("fix_policies");

  const [data, setData] = useState({});
  const [busy, setBusy] = useState("");
  const [repairPreview, setRepairPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [billsOpen, setBillsOpen] = useState(false);
  const [billRows, setBillRows] = useState([]);
  const [billNotice, setBillNotice] = useState("");
  const [billReason, setBillReason] = useState("");
  const [billFilter, setBillFilter] = useState({
    search: "",
    date_from: "",
    date_to: "",
    include_paid: false,
  });

  const load = async () => {
    const response = await axiosInstance.get("system-operations/status/");
    setData(response.data || {});
  };

  useEffect(() => {
    if (!canOperate) return undefined;

    load().catch(() => {});
    const timer = window.setInterval(() => {
      load().catch(() => {});
    }, busy ? 1000 : 30000);

    return () => window.clearInterval(timer);
  }, [canOperate, busy]);

  const run = async (key, url, success) => {
    setBusy(key);
    try {
      const response = await axiosInstance.post(url, {});
      toast.success(success);
      await load();
      return response.data;
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "The system operation could not be completed."
      );
      return null;
    } finally {
      setBusy("");
    }
  };

  const previewFix = async () => {
    const result = await run(
      "FIX PREVIEW",
      "system-operations/fix-policies/preview/",
      "Policy repair preview completed."
    );
    if (result) {
      setRepairPreview(result);
      setOpen(true);
    }
  };

  const applyFix = async () => {
    if (
      !window.confirm(
        "Apply the previewed policy repairs? Every change will be audited."
      )
    ) {
      return;
    }
    setBusy("FIX APPLY");
    try {
      const response = await axiosInstance.post(
        "system-operations/fix-policies/apply/",
        { confirmed: true }
      );
      toast.success(
        `${response.data.changed_records} policy record(s) repaired.`
      );
      setRepairPreview(response.data);
      await load();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          "Policy repairs could not be applied."
      );
    } finally {
      setBusy("");
    }
  };

  const openFixBills = () => {
    setBillsOpen(true);
    setBillRows([]);
    setBillNotice("");
    setBillReason("");
  };

  const searchBills = async () => {
    setBusy("FIX BILLS SEARCH");
    try {
      const response = await axiosInstance.post(
        "system-operations/fix-bills/preview/",
        billFilter
      );
      setBillRows(
        (response.data.results || []).map((row) => ({
          ...row,
          selected: false,
          corrected_balance: row.saved_balance,
        }))
      );
      setBillNotice(response.data.notice || "");
      if (response.data.truncated) {
        toast.warning("Only the first 500 bills are shown. Narrow the search.");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.[0] ||
          "Bills could not be loaded."
      );
    } finally {
      setBusy("");
    }
  };

  const updateBillRow = (billId, field, value) => {
    setBillRows((current) =>
      current.map((row) =>
        row.bill_id === billId ? { ...row, [field]: value } : row
      )
    );
  };

  const changedBillRows = billRows.filter(
    (row) =>
      row.selected &&
      Number(row.corrected_balance) !== Number(row.saved_balance)
  );

  const applyBillFixes = async () => {
    if (!changedBillRows.length) {
      toast.error("Select at least one bill whose corrected balance changed.");
      return;
    }
    if (billReason.trim().length < 10) {
      toast.error("Enter a clear correction reason of at least 10 characters.");
      return;
    }
    if (
      !window.confirm(
        `Correct ${changedBillRows.length} selected bill(s)? This creates a permanent audit record and does not create payments.`
      )
    ) {
      return;
    }
    setBusy("FIX BILLS APPLY");
    try {
      const response = await axiosInstance.post(
        "system-operations/fix-bills/apply/",
        {
          confirmed: true,
          reason: billReason.trim(),
          corrections: changedBillRows.map((row) => ({
            bill_id: row.bill_id,
            expected_current_balance: row.saved_balance,
            corrected_balance: row.corrected_balance,
          })),
        }
      );
      toast.success(`${response.data.changed} bill(s) corrected and audited.`);
      await searchBills();
      setBillReason("");
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.[0] ||
          "Bill corrections could not be applied."
      );
    } finally {
      setBusy("");
    }
  };

  const activeOperation =
    busy === "FIX PREVIEW"
      ? data["FIX POLICIES PREVIEW"]
      : busy === "FIX APPLY"
        ? data["FIX POLICIES APPLY"]
        : busy === "STATUS CHECK"
          ? data["STATUS CHECK"]
          : busy === "BILLING"
            ? data.BILLING
            : null;

  const activeTotal = Number(activeOperation?.total_records || 0);
  const activeProcessed = Number(
    activeOperation?.processed_records || 0
  );
  const activePercent =
    activeOperation?.progress_percent ??
    (activeTotal
      ? Math.round((activeProcessed / activeTotal) * 100)
      : 0);

  if (!canOperate) return null;

  return (
    <>
      <footer className="system-operations-bar">
        <div className="system-operation-statuses">
          {["BILLING", "STATUS CHECK"].map((key) => {
            const runData = data[key];
            return (
              <article key={key}>
                <span className={`system-operation-dot ${runData?.status?.toLowerCase().replaceAll(" ", "-") || ""}`} />
                <div>
                  <strong>{LABELS[key]}</strong>
                  <small>{detail(runData)}</small>
                </div>
              </article>
            );
          })}
        </div>

        {busy && (
          <div className="system-operation-progress" aria-live="polite">
            <div>
              <strong>
                {busy === "FIX PREVIEW"
                  ? "Checking policies"
                  : busy === "FIX APPLY"
                    ? "Applying repairs"
                    : busy === "FIX BILLS SEARCH"
                      ? "Finding bills"
                      : busy === "FIX BILLS APPLY"
                        ? "Correcting bills"
                    : busy === "STATUS CHECK"
                      ? "Checking statuses"
                      : "Running billing"}
              </strong>
              <span>
                {activeTotal
                  ? `${activeProcessed} / ${activeTotal} · ${activePercent}%`
                  : "Preparing..."}
              </span>
            </div>
            <progress
              max="100"
              value={Math.max(0, Math.min(100, activePercent))}
            />
          </div>
        )}

        <div className="system-operation-actions">
          <button
            onClick={() =>
              run(
                "BILLING",
                "system-operations/run-billing/",
                "Automatic billing completed."
              )
            }
            disabled={Boolean(busy)}
          >
            <i className="bi bi-receipt" />
            {busy === "BILLING" ? "Running..." : "Run billing"}
          </button>
          <button
            onClick={() =>
              run(
                "STATUS CHECK",
                "system-operations/run-status-check/",
                "Policy status check completed."
              )
            }
            disabled={Boolean(busy)}
          >
            <i className="bi bi-shield-check" />
            {busy === "STATUS CHECK" ? "Running..." : "Status check"}
          </button>
          <button
            className="fix"
            onClick={previewFix}
            disabled={Boolean(busy)}
          >
            <i className="bi bi-tools" />
            {busy === "FIX PREVIEW" ? "Checking..." : "Fix policies"}
          </button>
          {canFixBills && (
            <button
              className="fix-bills"
              onClick={openFixBills}
              disabled={Boolean(busy)}
            >
              <i className="bi bi-receipt-cutoff" />
              Fix bills
            </button>
          )}
        </div>
      </footer>

      {open && repairPreview && (
        <div className="system-operations-modal">
          <section>
            <header>
              <div>
                <span>POLICY CONSISTENCY</span>
                <h2>Fix Policies</h2>
                <p>
                  Preview identified inconsistencies before any repair is
                  applied.
                </p>
              </div>
              <button onClick={() => setOpen(false)}>×</button>
            </header>

            <div className="repair-summary">
              <article>
                <span>Policies checked</span>
                <strong>
                  {repairPreview.summary?.policies_checked ??
                    repairPreview.total_records}
                </strong>
              </article>
              <article>
                <span>Require repairs</span>
                <strong>{repairPreview.summary?.policies_requiring_repairs || 0}</strong>
              </article>
              <article>
                <span>Errors</span>
                <strong>{repairPreview.errors?.length || 0}</strong>
              </article>
              {(repairPreview.summary?.currency_review_policies || 0) > 0 && (
                <article>
                  <span>Currency review</span>
                  <strong>
                    {repairPreview.summary.currency_review_policies}
                  </strong>
                </article>
              )}
            </div>

            <div className="repair-list">
              {(repairPreview.summary?.results || []).length ? (
                repairPreview.summary.results.map((policy) => (
                  <article key={policy.policy_id}>
                    <header>
                      <strong>{policy.policy_code}</strong>
                      <span>{policy.issue_count} repair group(s)</span>
                    </header>
                    {policy.issues.map((issue, index) => (
                      <div key={`${issue.type}-${index}`}>
                        <b>{issue.type}</b>
                        <p>{issue.message}</p>
                      </div>
                    ))}
                  </article>
                ))
              ) : (
                <div className="repair-empty">
                  No policy inconsistencies were found.
                </div>
              )}
            </div>

            <footer>
              <button onClick={() => setOpen(false)}>Close</button>
              {(repairPreview.summary?.policies_requiring_repairs || 0) >
                0 && (
                <button
                  className="primary"
                  onClick={applyFix}
                  disabled={Boolean(busy)}
                >
                  {busy === "FIX APPLY"
                    ? "Applying repairs..."
                    : "Apply approved repairs"}
                </button>
              )}
            </footer>
          </section>
        </div>
      )}

      {billsOpen && (
        <div className="system-operations-modal fix-bills-modal">
          <section>
            <header>
              <div>
                <span>CONTROLLED BILL RECONCILIATION</span>
                <h2>Fix Bills</h2>
                <p>
                  Correct verified bill balances without inventing a payment,
                  receipt, commission or EcoSure transaction.
                </p>
              </div>
              <button onClick={() => setBillsOpen(false)}>×</button>
            </header>

            <div className="fix-bills-search">
              <label className="wide">
                <span>Policy number or policyholder</span>
                <input
                  value={billFilter.search}
                  onChange={(event) =>
                    setBillFilter({ ...billFilter, search: event.target.value })
                  }
                  placeholder="e.g. HFAS000110 or NTOKOZO NDLOVU"
                />
              </label>
              <label>
                <span>From bill date</span>
                <input
                  type="date"
                  value={billFilter.date_from}
                  onChange={(event) =>
                    setBillFilter({ ...billFilter, date_from: event.target.value })
                  }
                />
              </label>
              <label>
                <span>To bill date</span>
                <input
                  type="date"
                  value={billFilter.date_to}
                  onChange={(event) =>
                    setBillFilter({ ...billFilter, date_to: event.target.value })
                  }
                />
              </label>
              <label className="fix-bills-check">
                <input
                  type="checkbox"
                  checked={billFilter.include_paid}
                  onChange={(event) =>
                    setBillFilter({ ...billFilter, include_paid: event.target.checked })
                  }
                />
                <span>Include paid bills</span>
              </label>
              <button onClick={searchBills} disabled={Boolean(busy)}>
                <i className="bi bi-search" />
                {busy === "FIX BILLS SEARCH" ? "Loading..." : "Find bills"}
              </button>
            </div>

            {billNotice && <div className="fix-bills-notice">{billNotice}</div>}

            <div className="fix-bills-table-wrap">
              <table className="fix-bills-table">
                <thead>
                  <tr>
                    <th>Fix</th>
                    <th>Policy / holder</th>
                    <th>Bill</th>
                    <th>Bill amount</th>
                    <th>Saved balance</th>
                    <th>Linked payments</th>
                    <th>Linked ledger balance</th>
                    <th>Corrected balance</th>
                    <th>Change</th>
                  </tr>
                </thead>
                <tbody>
                  {billRows.map((row) => {
                    const change =
                      Number(row.corrected_balance || 0) -
                      Number(row.saved_balance || 0);
                    return (
                      <tr key={row.bill_id}>
                        <td>
                          <input
                            type="checkbox"
                            checked={row.selected}
                            onChange={(event) =>
                              updateBillRow(row.bill_id, "selected", event.target.checked)
                            }
                          />
                        </td>
                        <td>
                          <strong>{row.policy_code}</strong>
                          <small>{row.policyholder || "—"}</small>
                        </td>
                        <td>
                          <strong>{row.bill_code}</strong>
                          <small>{row.bill_date} · {row.bill_status}</small>
                        </td>
                        <td>{row.currency_code} {row.bill_amount}</td>
                        <td>{row.currency_code} {row.saved_balance}</td>
                        <td>
                          {row.currency_code} {row.recorded_allocations}
                          <small>{row.linked_allocation_count} allocation(s)</small>
                        </td>
                        <td>{row.currency_code} {row.linked_ledger_balance}</td>
                        <td>
                          <input
                            type="number"
                            min="0"
                            max={row.bill_amount}
                            step="0.01"
                            value={row.corrected_balance}
                            onChange={(event) =>
                              updateBillRow(
                                row.bill_id,
                                "corrected_balance",
                                event.target.value
                              )
                            }
                          />
                        </td>
                        <td className={change === 0 ? "" : change < 0 ? "down" : "up"}>
                          {change > 0 ? "+" : ""}{change.toFixed(2)}
                        </td>
                      </tr>
                    );
                  })}
                  {!billRows.length && (
                    <tr>
                      <td colSpan="9" className="fix-bills-empty">
                        Search for a policy or bill-date range. No database
                        changes occur until selected corrections are confirmed.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="fix-bills-reason">
              <label>
                <span>Required correction reason</span>
                <textarea
                  value={billReason}
                  onChange={(event) => setBillReason(event.target.value)}
                  placeholder="State what evidence proves the corrected balance."
                />
              </label>
              <div>
                <strong>{changedBillRows.length}</strong>
                <span>selected change(s)</span>
              </div>
            </div>

            <footer>
              <button onClick={() => setBillsOpen(false)}>Close</button>
              <button
                className="primary"
                onClick={applyBillFixes}
                disabled={Boolean(busy) || !changedBillRows.length}
              >
                {busy === "FIX BILLS APPLY"
                  ? "Applying corrections..."
                  : "Apply selected corrections"}
              </button>
            </footer>
          </section>
        </div>
      )}
    </>
  );
}

export default SystemOperationsBar;
